import { fg } from '@atlaskit/platform-feature-flags/fg';

import { ProductKeys1P, type ProductKeys } from '../common/constants/types';
import { isForgeConnectorProductKey } from '../common/types/schemas/types';
import { ThirdPartyConfigsSync as ThirdPartyConfigs } from '../common/ui/configs/sync';

type GetSupportedAccessibleProductsArgs = {
	supportedProducts: ProductKeys[];

	/**
	 * DEPRECATED - Do not use. There shouldn't be any usages of this and can be cleaned up.
	 */
	products?: ProductKeys[];
	/**
	 * The list of products a user is authorized to use. Typed as
	 * `(ProductKeys | string)[]` because some upstream sources (e.g.
	 * `parseCombinedConfig` in `services/combined-config/parse.ts`, and
	 * the search-page bootstrap that reads first-party product names
	 * straight from the aggregator response) hand us raw strings. The
	 * function below normalises by filtering against `supportedProducts`,
	 * so unknown strings drop out safely.
	 */
	accessibleProducts?: (ProductKeys | string)[];
};

/**
 * Utility to get the accessible products based on the lifecycle of fetching
 * the configuration of what a user is authorised to use.
 *
 * NOTE: The `products` parameter is deprecated as we are removing it as a
 * prop from the root <SearchPage/>
 *
 * @param supportedProducts Record of products supported by FPS
 * @param products Overrides the list of products a user can access
 * @param accessibleProducts The list of products a user is authorized to use. Initially
 * undefined while the user configuration is being retrieved.
 */
export const getSupportedAccessibleProducts = ({
	supportedProducts,
	products,
	accessibleProducts,
}: GetSupportedAccessibleProductsArgs): ProductKeys[] => {
	if (!accessibleProducts) {
		return [];
	}

	const fgControlledProducts: Partial<Record<ProductKeys, boolean>> = {
		[ProductKeys1P.Talent]: fg('talent_global_search'),
	};

	const disabledProducts3PFromConfig: ProductKeys[] = [];
	ThirdPartyConfigs.forEach((config, productKey3P) => {
		if (config && !config.isEnabled()) {
			disabledProducts3PFromConfig.push(productKey3P as ProductKeys);
		}
	});

	const accessibleAndSupportedProducts: ProductKeys[] = (accessibleProducts as ProductKeys[])
		.filter(
			(productKey) =>
				!disabledProducts3PFromConfig.includes(productKey) &&
				(productKey in fgControlledProducts ? fgControlledProducts[productKey] : true),
		)
		.filter((productKey) => {
			// Forge connectors are dynamically added and won't be in supportedProducts
			if (isForgeConnectorProductKey(productKey)) {
				return true;
			}
			// For hardcoded products, check if they're in supportedProducts
			return supportedProducts.includes(productKey as ProductKeys);
		});

	if (products) {
		const allowedProducts = products.filter((product) =>
			accessibleAndSupportedProducts.includes(product),
		);

		return allowedProducts;
	} else {
		if (
			//TODO: move this logic to xpsearch aggregator before release with the correct conditions
			// eslint-disable-next-line @atlaskit/platform/no-preconditioning
			accessibleAndSupportedProducts.includes(ProductKeys1P.JiraServiceManagement as ProductKeys) &&
			fg('assets_rovo_search_all') && // add assets as a product if JSM license is present, but this is until assets becomes a platform
			!fg('aurum-assets-as-a-platform') // disable this when assets becomes a platform and we will start getting assets as its product
		) {
			return [...accessibleAndSupportedProducts, ProductKeys1P.Assets as ProductKeys];
		}
		return accessibleAndSupportedProducts;
	}
};
